import { Package, Plane, CalendarDays, Car, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AirportImage from '../../assets/Airport.png';
import SwapImages from '../../assets/swapimages.png';
import MobileImage from '../../assets/mobile.png';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setPickupLocation,
  setDropoffLocation,
  setPickupMode,
} from '../../features/location/locationSlice';
import {
  selectDropoffLocation,
  selectPickupLocation,
  selectPickupMode,
} from '../../features/location/selectors';
import { PlacesAutocompleteInput } from '../inputs/PlacesAutocompleteInput';

export function SwapHero({ isGuest = false }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pickupMode = useAppSelector(selectPickupMode);

  // Determine mode based on URL path or localStorage
  const [isFlexyRideMode, setIsFlexyRideMode] = useState(() => {
    if (location.pathname.startsWith('/flexy-ride')) {
      return true;
    }
    const savedMode = localStorage.getItem('flexySwap_activeMode');
    return savedMode === 'flexyRide';
  });

  // Update mode when URL or localStorage changes
  useEffect(() => {
    if (location.pathname.startsWith('/flexy-ride')) {
      setIsFlexyRideMode(true);
    } else {
      const savedMode = localStorage.getItem('flexySwap_activeMode');
      setIsFlexyRideMode(savedMode === 'flexyRide');
    }
  }, [location.pathname]);

  // Listen for storage changes (when MainNavbar updates mode)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedMode = localStorage.getItem('flexySwap_activeMode');
      if (!location.pathname.startsWith('/flexy-ride')) {
        setIsFlexyRideMode(savedMode === 'flexyRide');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom event for same-window updates
    window.addEventListener('flexySwap_modeChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('flexySwap_modeChange', handleStorageChange);
    };
  }, [location.pathname]);

  const pickupModes = isFlexyRideMode
    ? [
        { id: 'driver', label: t('home.imDriver'), icon: Car },
        { id: 'passenger', label: t('home.imPassenger'), icon: User },
      ]
    : [
        { id: 'sender', label: t('home.imSender'), icon: Package },
        { id: 'traveler', label: t('home.imTraveler'), icon: Plane },
      ];
  const pickupLocation = useAppSelector(selectPickupLocation);
  const dropoffLocation = useAppSelector(selectDropoffLocation);
  const [fromSelection, setFromSelection] = useState(pickupLocation);
  const [toSelection, setToSelection] = useState(dropoffLocation);
  const [fromDescription, setFromDescription] = useState(
    pickupLocation?.description || '',
  );
  const [toDescription, setToDescription] = useState(
    dropoffLocation?.description || '',
  );
  const [departureDate, setDepartureDate] = useState('');

  // Get the currently selected mode ID (not Redux state, but the actual button selection)
  const getSelectedModeId = () => {
    if (isFlexyRideMode) {
      // In Flexy Ride mode, map Redux state back to mode IDs
      return pickupMode === 'traveler' ? 'passenger' : 'driver';
    } else {
      // In Courier mode, Redux state matches mode IDs
      return pickupMode || 'sender';
    }
  };

  useEffect(() => {
    setFromSelection(pickupLocation);
    setFromDescription(pickupLocation?.description || '');
  }, [pickupLocation]);

  useEffect(() => {
    setToSelection(dropoffLocation);
    setToDescription(dropoffLocation?.description || '');
  }, [dropoffLocation]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (fromDescription.trim()) {
      params.set('from', fromDescription.trim());
    }
    if (toDescription.trim()) {
      params.set('to', toDescription.trim());
    }
    if (departureDate) {
      params.set('date', departureDate);
    }

    // Get the currently selected mode ID
    const selectedModeId = getSelectedModeId();

    let basePath;
    if (isFlexyRideMode) {
      // Flexy Ride mode
      basePath =
        selectedModeId === 'passenger'
          ? '/flexy-ride/passenger'
          : '/flexy-ride/driver';
    } else {
      // Courier mode
      basePath =
        selectedModeId === 'traveler' ? '/trips/traveler' : '/trips/sender';
    }

    const target = params.toString()
      ? `${basePath}?${params.toString()}`
      : basePath;
    navigate(target);
  };

  const searchDisabled = !fromDescription.trim() || !toDescription.trim();

  return (
    <section className="bg-white text-gray-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto flex max-w-7xl flex-col gap-10 px-4 py-20 lg:flex-row lg:items-center lg:gap-12">
        <div className="w-full lg:w-5/12">
          <p className="text-xs uppercase tracking-[0.7em] text-yellow-500 dark:text-yellow-400">
            {isFlexyRideMode ? t('home.flexyRide') : t('home.packageSwap')}
          </p>
          <h1 className="mt-3 text-2xl font-black leading-tight text-black dark:text-white lg:text-[42px] lg:leading-[1.1] mb-10">
            {isFlexyRideMode
              ? t('home.findRidesAnywhere')
              : t('home.sendPackagesAnywhere')}
          </h1>

          <div className={`  ${isGuest ? '' : ''}`}>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {pickupModes.map(mode => {
                // Map Flexy Ride modes to Redux state values
                const reduxMode = isFlexyRideMode
                  ? mode.id === 'driver'
                    ? 'sender'
                    : 'traveler'
                  : mode.id;
                const isSelected = isFlexyRideMode
                  ? (mode.id === 'driver' && pickupMode === 'sender') ||
                    (mode.id === 'passenger' && pickupMode === 'traveler')
                  : pickupMode === mode.id;

                return (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => dispatch(setPickupMode(reduxMode))}
                    className={`flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold ${
                      isSelected
                        ? 'bg-yellow-400 text-black shadow-md dark:bg-yellow-500 dark:text-black'
                        : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300'
                    }`}
                  >
                    <mode.icon className="h-8 w-8" />
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <div className="relative mt-4 space-y-3 text-sm">
              <span className="absolute left-[-1px] top-[34px] flex flex-col items-center">
                <span className="h-4 w-4 rounded-full border-2 border-gray-900 bg-white dark:border-slate-200 dark:bg-slate-800" />
                <span className="mt-2 h-[57px] w-px bg-gray-900 dark:bg-slate-200" />
                <span className="mt-2 h-4 w-4 rounded-sm bg-gray-900 dark:bg-slate-200" />
              </span>
              <div className="pl-6">
                <PlacesAutocompleteInput
                  label={t('home.from')}
                  placeholder={t('home.fromPlaceholder')}
                  value={fromSelection}
                  onSelect={selection => {
                    setFromSelection(selection);
                    setFromDescription(selection?.description || '');
                    dispatch(setPickupLocation(selection));
                  }}
                  onInputChange={value => setFromDescription(value)}
                />
              </div>
              <div className="pl-6">
                <PlacesAutocompleteInput
                  label={t('home.to')}
                  placeholder={t('home.toPlaceholder')}
                  value={toSelection}
                  onSelect={selection => {
                    setToSelection(selection);
                    setToDescription(selection?.description || '');
                    dispatch(setDropoffLocation(selection));
                  }}
                  onInputChange={value => setToDescription(value)}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 dark:text-slate-400">
                  {t('home.dateAndTime')}
                </label>
                <div className="mt-1.5 flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900">
                  <CalendarDays className="h-4 w-4 text-gray-400 dark:text-slate-500" />
                  <input
                    type="date"
                    className="w-full bg-transparent text-sm text-gray-900 focus:outline-none dark:text-slate-100"
                    value={departureDate}
                    onChange={event => setDepartureDate(event.target.value)}
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-gray-500 dark:text-slate-400">
                  {t('home.resultsWithinDays')}
                </p>
              </div>
            </div>
            <button
              type="button"
              className={`mt-4 w-full rounded-2xl border border-gray-900 px-5 py-2.5 text-sm font-semibold transition dark:border-slate-200 ${
                searchDisabled
                  ? 'cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-slate-800 dark:text-slate-500'
                  : isGuest
                  ? 'text-gray-900 hover:bg-black hover:text-yellow-400 dark:text-slate-100 dark:hover:bg-black dark:hover:text-yellow-400'
                  : 'text-gray-900 hover:bg-gray-100 dark:text-slate-100 dark:hover:bg-slate-800'
              }`}
              disabled={searchDisabled}
              onClick={() => {
                if (isGuest) {
                  navigate('/auth/login');
                  return;
                }
                handleSearch();
              }}
            >
              {isGuest ? t('home.signInToFindTrips') : t('home.findTrips')}
            </button>
          </div>
        </div>

        <div className="relative w-full lg:w-7/12 flex justify-end lg:justify-center">
          <img
            src={isFlexyRideMode ? SwapImages : AirportImage}
            alt={
              isFlexyRideMode
                ? 'People sharing rides'
                : 'Traveler handing a parcel at the airport'
            }
            className="w-full rounded-[24px] object-cover mt-20"
            loading="lazy"
          />
        </div>
      </div>

      <div className="mx-auto max-w-7xl border-t border-gray-200 px-4 py-20 dark:border-slate-800">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-yellow-50 via-white to-yellow-50/50 p-8 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 md:p-12 lg:p-16">
          <div className="relative z-10 flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="w-full space-y-6 text-center lg:w-1/2 lg:text-left">
              <div className="inline-block rounded-full bg-yellow-400/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-yellow-600 dark:bg-yellow-500/20 dark:text-yellow-400">
                {t('home.mobileApp')}
              </div>
              <h2 className="text-4xl font-black text-gray-900 dark:text-white lg:text-5xl">
                {t('home.takeFlexySwap')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-slate-300">
                {t('home.downloadApp')}
              </p>
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center lg:justify-start">
                <a
                  href="https://apps.apple.com/app/flexyswap"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform hover:scale-105"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                    alt="Download on the App Store"
                    className="h-14 w-auto"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.flexyswap"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform hover:scale-105"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Google_Play_Store_badge_EN.svg/512px-Google_Play_Store_badge_EN.svg.png"
                    alt="Get it on Google Play"
                    className="h-14 w-auto"
                  />
                </a>
              </div>
            </div>
            <div className="relative w-full lg:w-1/2 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 -z-10 rounded-full bg-yellow-400/20 blur-3xl dark:bg-yellow-500/10" />
                <img
                  src={MobileImage}
                  alt="FlexySwap mobile app"
                  className="relative z-10 h-auto w-full max-w-xl object-contain drop-shadow-2xl"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
